import React from 'react';
import { Button, Checkbox, Divider, Dropdown, Form, Icon, Input, Loader, Message, Modal, Table } from 'semantic-ui-react';
import SmoothModal from './components/SmoothModal';
import api from '../utils/api';
import colors from '../utils/colors';

const clone = (o) => JSON.parse(JSON.stringify(o));
const slug = (name) => (name ?? '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30) || 'role';
const LOADING_HEIGHT = 420;

/**
 * Admin setup: which templates are rows (and their short alias), which roles are columns, and which event
 * fields each role links to. `templates` (from GET /services/setup) is usually prefetched by the page so this
 * opens instantly; if not, it loads here behind a fixed-height loader.
 */
export default class ServicesSettingsPopup extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			config: clone(props.config ?? { services: [], roles: [], version: 0 }),
			templates: props.templates ?? null,
			saving: false,
			error: null,
		};
	}

	componentDidMount = async () => {
		if (this.state.templates) return;
		try {
			const setup = await api.getServicesSetup();
			this.setState({ templates: setup.templates, config: clone(setup.config) });
		}
		catch (error) {
			this.setState({ templates: [], error: `Could not load templates: ${error}` });
		}
	}

	template = (templateId) => this.state.templates?.find(t => t.id === templateId);

	fieldOptions = (templateId) => (this.template(templateId)?.fields ?? []).map(f => ({
		key: f.name, value: f.name, text: f.count > 1 ? `${f.name} (×${f.count})` : f.name,
	}));

	/** A rotation on a linked field is ignored by the server, but it should be turned off to avoid confusion */
	rotationWarnings = (role) => Object.entries(role.fields ?? {}).flatMap(([templateId, names]) => {
		const template = this.template(templateId);
		return (template?.fields ?? []).filter(f => f.rotating && names.includes(f.name))
			.map(f => `${template.name}: "${f.name}" still has an auto-rotation. Services ignores it for linked fields; turn it off on the template to avoid confusion.`);
	});

	update = (fn) => this.setState(prev => { const config = clone(prev.config); fn(config); return { config, error: null }; });

	addService = (templateId) => {
		if (!templateId || this.state.config.services.some(s => s.templateId === templateId)) return;
		const template = this.template(templateId);
		this.update(c => c.services.push({ templateId, label: template?.name ?? 'Service', alias: '', order: c.services.length }));
	}

	move = (list, idx, delta) => this.update(c => {
		const items = c[list].sort((a, b) => a.order - b.order);
		const target = idx + delta;
		if (target < 0 || target >= items.length) return;
		[items[idx], items[target]] = [items[target], items[idx]];
		items.forEach((item, i) => item.order = i);
	});

	addRole = () => this.update(c => {
		const taken = new Set(c.roles.map(r => r.id));
		let id = slug(`role-${c.roles.length + 1}`);
		while (taken.has(id)) id = `${id}-x`;
		c.roles.push({ id, name: '', order: c.roles.length, notes: false, notify: false, fields: {} });
	});

	save = async () => {
		this.setState({ saving: true, error: null });
		try {
			const saved = await api.putServicesConfig(this.state.config);
			this.setState({ saving: false });
			this.props.onSaved(saved);
		}
		catch (error) {
			this.setState({ saving: false, error: `${error}` });
		}
	}

	renderBody = () => {
		const { config, templates } = this.state;
		const services = [...config.services].sort((a, b) => a.order - b.order);
		const roles = [...config.roles].sort((a, b) => a.order - b.order);
		const templateOptions = (templates ?? []).map(t => ({ key: t.id, value: t.id, text: t.name }));

		return (
			<Form>
				<h4>Services (rows)</h4>
				<p style={{ color: colors.darkGray }}>
					Each service is an event template; its dates come from the template's auto-create schedule.
					The alias is what the table shows. Leave it empty to show the start time (e.g. "9:00 AM").
				</p>
				<Table compact unstackable>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell>Name</Table.HeaderCell>
							<Table.HeaderCell>Alias</Table.HeaderCell>
							<Table.HeaderCell>Template</Table.HeaderCell>
							<Table.HeaderCell />
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{services.map((service, idx) =>
							<Table.Row key={service.templateId}>
								<Table.Cell width={5}>
									<Input fluid value={service.label} placeholder='Full name (filters, previews)'
										onChange={(e, { value }) => this.update(c => c.services.find(s => s.templateId === service.templateId).label = value)} />
								</Table.Cell>
								<Table.Cell width={3}>
									<Input fluid value={service.alias ?? ''} placeholder='Start time'
										onChange={(e, { value }) => this.update(c => c.services.find(s => s.templateId === service.templateId).alias = value)} />
								</Table.Cell>
								<Table.Cell width={5} style={{ color: colors.darkGray }}>{this.template(service.templateId)?.name ?? service.templateId}</Table.Cell>
								<Table.Cell width={3} textAlign='right'>
									<Button icon='arrow up' size='mini' basic disabled={idx === 0} onClick={() => this.move('services', idx, -1)} />
									<Button icon='arrow down' size='mini' basic disabled={idx === services.length - 1} onClick={() => this.move('services', idx, 1)} />
									<Button icon='trash' size='mini' basic onClick={() => this.update(c => {
										c.services = c.services.filter(s => s.templateId !== service.templateId);
										c.roles.forEach(r => delete r.fields[service.templateId]);
									})} />
								</Table.Cell>
							</Table.Row>
						)}
					</Table.Body>
				</Table>
				<Dropdown
					placeholder='Add a service (template)…'
					selection search
					options={templateOptions.filter(o => !config.services.some(s => s.templateId === o.value))}
					value={null}
					onChange={(e, { value }) => this.addService(value)}
				/>

				<Divider />

				<h4>Roles (columns)</h4>
				<p style={{ color: colors.darkGray }}>
					A role links one or more fields on each template. All linked fields always hold the same people:
					editing the role here, on the event, or in the Planner changes all of them, and a decline applies to all of them.
					Notes (topic / passage) are shown and edited only for roles that have them turned on, e.g. Scripture Reading and Preaching.
				</p>

				{roles.map((role, idx) =>
					<div key={role.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 4, padding: 12, marginBottom: 12 }}>
						<div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
							<Input placeholder='Role name' value={role.name} style={{ width: 220 }}
								onChange={(e, { value }) => this.update(c => c.roles.find(r => r.id === role.id).name = value)} />
							<Checkbox toggle label='Notes (topic / passage)' checked={!!role.notes}
								onChange={(e, { checked }) => this.update(c => c.roles.find(r => r.id === role.id).notes = checked)} />
							<Checkbox toggle label='Offer to text people to confirm' checked={!!role.notify}
								onChange={(e, { checked }) => this.update(c => c.roles.find(r => r.id === role.id).notify = checked)} />
							<div style={{ marginLeft: 'auto' }}>
								<Button icon='arrow up' size='mini' basic disabled={idx === 0} onClick={() => this.move('roles', idx, -1)} />
								<Button icon='arrow down' size='mini' basic disabled={idx === roles.length - 1} onClick={() => this.move('roles', idx, 1)} />
								<Button icon='trash' size='mini' basic onClick={() => this.update(c => c.roles = c.roles.filter(r => r.id !== role.id))} />
							</div>
						</div>

						<Table compact unstackable basic='very' style={{ marginTop: 8 }}>
							<Table.Body>
								{services.map(service =>
									<Table.Row key={service.templateId}>
										<Table.Cell width={5} style={{ color: colors.darkGray }}>{service.label}</Table.Cell>
										<Table.Cell width={11}>
											<Dropdown
												fluid multiple search selection
												placeholder='Not on this service'
												options={this.fieldOptions(service.templateId)}
												value={role.fields?.[service.templateId] ?? []}
												onChange={(e, { value }) => this.update(c => c.roles.find(r => r.id === role.id).fields[service.templateId] = value)}
											/>
										</Table.Cell>
									</Table.Row>
								)}
							</Table.Body>
						</Table>

						{this.rotationWarnings(role).map(w => <div key={w} style={{ color: colors.accent }}><Icon name='warning sign' />{w}</div>)}
					</div>
				)}
				<Button basic icon='plus' content='Add role' onClick={this.addRole} />
			</Form>
		);
	}

	render() {
		const { open, onClose } = this.props;
		const { templates, saving, error } = this.state;
		const loading = templates === null;

		return (
			<SmoothModal open={open} size='large' onClose={saving ? undefined : onClose} header='Services settings'>
				<Modal.Content scrolling style={{ minHeight: LOADING_HEIGHT }}>
					{loading
						? <div style={{ height: LOADING_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader active inline='centered' content='Loading templates…' /></div>
						: this.renderBody()
					}
					{error && <Message error>{error}</Message>}
				</Modal.Content>
				<Modal.Actions>
					<Button onClick={onClose} disabled={saving}>Cancel</Button>
					<Button primary loading={saving} disabled={saving || loading} onClick={this.save}>Save settings</Button>
				</Modal.Actions>
			</SmoothModal>
		);
	}
}
